/**
 * Google OAuth Callback Handler
 * 
 * Handles the callback from custom Google OAuth.
 * Reads session info from URL params and redirects to appropriate page.
 * Uses unified callback handler for consistent behavior across all OAuth providers.
 */
import React, { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../config/api';
import {
  processOAuthCallback,
  executeCallbackNavigation,
  parseOAuthUrlParams,
} from '../utils/oauthCallbackHandler';

const GoogleAuthCallback = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loginFromOAuth } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processCallback = async () => {
      console.log('[GoogleAuthCallback] Processing callback');
      
      // Parse URL params using unified parser
      const params = parseOAuthUrlParams(searchParams);
      
      // Handle error from OAuth provider
      if (params.error) {
        console.error('[GoogleAuthCallback] OAuth error:', params.error, params.errorMessage);
        toast.error(params.errorMessage || 'Authentication failed. Please try again.');
        navigate('/login');
        return;
      }

      console.log('[GoogleAuthCallback] Parsed params:', {
        hasSessionToken: !!params.sessionToken,
        isNewUser: params.isNewUser,
        userRole: params.userRole,
        hasCheckoutUrl: !!params.checkoutUrl,
        existingUserFromSignup: params.existingUserFromSignup,
      });

      // Set auth state BEFORE navigation
      // Google OAuth only provides URL params, so loginFromOAuth will fetch from /api/auth/me
      if (params.sessionToken && !params.checkoutUrl) {
        console.log('[GoogleAuthCallback] Setting auth state via loginFromOAuth (will fetch from server)');
        await loginFromOAuth(null, null);
      }

      // Process callback using unified handler for navigation
      const result = processOAuthCallback({
        sessionToken: params.sessionToken,
        isNewUser: params.isNewUser,
        userEmail: params.userEmail,
        userRole: params.userRole,
        onboardingCompleted: params.onboardingCompleted,
        existingUserFromSignup: params.existingUserFromSignup,
        checkoutUrl: params.checkoutUrl,
        accessRedirect: params.accessRedirect,
        accessMessage: params.accessMessage,
        removedFromOrg: params.removedFromOrg,
        availableOrgsJson: params.availableOrgsJson,
      });

      console.log('[GoogleAuthCallback] Navigation result:', result);

      // Execute navigation (handles pending invite acceptance if needed)
      await executeCallbackNavigation(result, navigate, API_BASE_URL);
    };

    processCallback();
  }, [navigate, searchParams, loginFromOAuth]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50 flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 animate-pulse">
          <Loader2 className="w-8 h-8 text-white animate-spin" />
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          Completing sign in...
        </h2>
        <p className="text-gray-600">
          Please wait while we set up your session
        </p>
      </div>
    </div>
  );
};

export default GoogleAuthCallback;
